import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
} from "@models";
import { 
  Character, 
  Proficiency,
  Tool,
  Weapon,
  // Item,
  CharacterSheetInterface,
  CharacterFeature,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import FloatMenu from "@components/input/menus/FloatMenu";
import LoadingBox from "@components/input/LoadingBox";
import CharacterArmorTrainingCustomizations from '../customizations/CharacterArmorTrainingCustomizations';
import CharacterWeaponProfCustomizations from '../customizations/CharacterWeaponProfCustomizations';
import CharacterWeaponMasteryCustomizations from '../customizations/CharacterWeaponMasteryCustomizations';
import CharacterToolProfCustomizations from '../customizations/CharacterToolProfCustomizations';

interface Props {
  char: Character;
  char_interface: CharacterSheetInterface;
  disabled: boolean;
}

// const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterProficiencies({
  char,
  char_interface,
  disabled,
}: Props) {
  const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [weapons, setWeapons] = useState<Weapon[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("Features");
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    // console.log("Proficiencies");
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      // character.subscription_holder.subscribe("Skill", mounted, recalculated);
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = () => {
    setTools(data_util.tools);
    setWeapons((data_util.items).filter(o => o instanceof Weapon) as Weapon[]);
  }

  const renderProficiencyFeatures = () => {
    const proficiency_features = char.mind.proficiency_features.filter(cf => 
      cf.floating_feature instanceof Proficiency && 
        common_util.anyOverlaps(cf.floating_feature.proficiency_types, ["Tool","Armor","Weapon","Special"]));
    const wm_features = char.mind.weapon_mastery_features;
    const the_features = [...proficiency_features, ...wm_features];
    const use_features: CharacterFeature[] = [];
    for (const cf of the_features) {
      if (use_features.filter(o => o.cfb_id === cf.cfb_id).length === 0) {
        use_features.push(cf);
      }
    }
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Proficiency Features
        </Grid>
        { use_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = char.char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  char={char}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderView = () => {
    switch(view) {
      case "Features": return renderProficiencyFeatures();
      case "Customize Armors": 
        return (
          <CharacterArmorTrainingCustomizations char={char_interface} />
        );
      case "Customize Weapons": 
        return (
          <CharacterWeaponProfCustomizations char={char_interface} />
        );
      case "Customize Tools": 
        return (
          <CharacterToolProfCustomizations char={char_interface} />
        );
      case "Customize Weapon Masteries": 
        return (
          <CharacterWeaponMasteryCustomizations char={char_interface} />
        );
    }
    return null;
  }

  const renderControls = () => {
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Proficiencies
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={[
              "Features",
              "Customize Armors","Customize Weapons","Customize Tools",
              "Customize Weapon Masteries"
            ]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }
  
  if (!fetched) {
    return (<LoadingBox />);
  } else {
    // console.log(character.weapon_masteries);
    return (
      <Grid container spacing={0} direction="row" className="curved_border bg_black_transp">
        <Grid
          className={`centered header ${ disabled ? "" : "clickable" }`}
          onClick={() => {
            if (!disabled) {
              setOpen(true); 
            } 
          }}
          size={{
            xs: 12
          }}>
          Proficiencies
        </Grid>
        { char_interface.mind.armor_proficiencies.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px", 
                paddingLeft: "4px" 
              }}
              size={{
                xs: 12
              }}>
              Armor
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              <DisplayObjects type="armor_type" ids={char_interface.mind.armor_proficiencies.map(o => { return o.proficiency_id; })} />
            </Grid>
          </>
        }
        { (char_interface.mind.weapon_proficiencies.length > 0 || 
            char_interface.mind.special_proficiencies.length > 0) && 
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              Weapons
            </Grid>
            { char_interface.mind.weapon_proficiencies.length > 0 &&
              <Grid
                style={{
                  fontSize: "11px", paddingLeft: "4px"
                }}
                size={{
                  xs: 12
                }}>
                <DisplayObjects types={["weapon_keyword","item"]} ids={char_interface.mind.weapon_proficiencies.map(o => { return o.proficiency_id; })} />
              </Grid>
            }
          </>
        }
        { char.mind.weapon_masteries.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px" 
              }}
              size={{
                xs: 12
              }}>
              Weapon Masteries
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              { char.mind.standard_weapon_masteries.map((cwm, key) => {
                const limit = cwm.weapon_limits.find(o => o.isEmpty(["item_ids"]));
                if (limit) {
                  // console.log(cwm);
                  // console.log(limit);
                  return (
                    <span key={cwm.true_id}>
                      { key > 0 && "; " }
                      <DisplayObjects type="item" ids={limit.item_ids} />:&nbsp;
                      <DisplayObjects type="weapon_mastery_property" ids={[cwm.source_id]} />
                    </span>
                  );
                }
                return null;
              })}
            </Grid>
          </>
        }
        { char_interface.mind.tool_proficiencies.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              Tools
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              <DisplayObjects type="tool" ids={char_interface.mind.tool_proficiencies.map(o => { return o.proficiency_id; })} />
            </Grid>
          </>
        }
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            char.subscription_holder.modalClosed();
          }}>
          { renderControls() }
        </ModalOrDrawer>
      </Grid>
    );
  }
}
